<template>
    <div class="card">
      <div class="bg-blue-light p-1">
            <TitleButton
                title="Add Performance Appraisal"
                btnTitle="Add New"
                :showAddNew="false"
                :showBtn="true"
                @onClickCloseButton="navigateToListPage"
            />
        </div>
    </div>

    <div class="card">
      <div class="col-12 pt-2">
        <div class="d-flex justify-content-end px-2">
          <button
              @click="goToPrint"
              :disabled="loading"
              class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
          >
            Print
          </button>
        </div>
      </div>

        <div class="row p-2">
          <div class="col-12 col-lg-4">
            <div class="d-flex justify-content-start">
              <div class="text-white d-flex justify-content-center align-items-center rounded-circle"
                   style="width: 60px; height: 60px;">
                <img width="60" height="60" :src="contactProfile.photo" alt="">
              </div>
              <div class="ms-3">
                <h5 class="mb-0">{{ contactProfile.full_name }}</h5>
                <div class="pt-2 profile-info">
                  <p><strong>Department:</strong> {{ contactProfile.department }}</p>
                  <p><strong>Designation:</strong> {{ contactProfile.designation }}</p>
                  <p><strong>ID:</strong> {{ contactProfile.serial_no }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>

    <div class="card" v-for="(component, i) in componentList" :key="i">
        <div class="row p-2">
          <div class="bg-light border-bottom p-1">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0 fw-bold">{{ component.name }}</h5>
              </div>
              <div v-if="component.calculation_type != 'target'" class="d-flex align-items-center gap-2">
                <span class="fw-semibold">{{ component.weight }}%</span>
              </div>
              <button
                  @click="onClickOpenTargetModal(component)"
                  v-if="component.calculation_type == 'target' && isSupervisorAuthorized"
                  class="btn btn-primary btn-sm"
                  :disabled="loading"
              >
                Target
              </button>
            </div>
          </div>

          <div class="row p-2">
            <div class="col-12 col-lg-8 mb-3 mb-lg-0" v-if="component.calculation_type != 'target'">
              <TreeComponentItem
                  v-for="item in component.active_child"
                  :key="item.name"
                  :parent="component"
                  :node="item"
              />
            </div>

            <div class="col-12 mb-3 mb-lg-0" v-if="component.calculation_type == 'target'">
              <TargetAchievementTreeComponentForm
                  v-for="item in component.active_child"
                  :key="item.name"
                  :parent="component"
                  :isSupervisorAuthorized="isSupervisorAuthorized"
                  :node="item"
              />
            </div>
          </div>

        </div>
    </div>

  <div class="card" v-for="(component, i) in performanceAppraisalData" :key="i">
    <div class="row p-2">
      <div class="bg-light border-bottom p-1">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h5 class="mb-0 fw-bold">{{ component.performance_component_setup.name }}</h5>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="fw-semibold">{{ component.performance_component_setup.weight }}%</span>
          </div>
        </div>
      </div>

      <div class="row p-2">
        <div class="col-12 col-lg-8 mb-3 mb-lg-0">
          <PerformanceComponentForm
              v-for="item in component.performance_component_setup.active_child"
              :key="item.name"
              :parent="component.performance_component_setup"
              :node="item"
              :performanceAppraisalNote="performanceAppraisalNote"
              :performanceAppraisal="component"
          />
        </div>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="col-12 p-2">
      <div class="mb-2">
        <label for="note" class="form-label fw-bold">Notes:</label>
        <textarea v-model="formData.remarks" placeholder="Remarks" class="form-control" rows="2"
                  id="note"></textarea>
      </div>
    </div>
    <div class="mt-2 d-flex justify-content-start justify-content-sm-center px-2 gap-1 flex-wrap pb-2">
      <button :disabled="saveButtonLoader || saveNewButtonLoader"
              @click="handleSubmit"
              class="btn btn-primary">
        Save
      </button>
      <button @click="navigateToListPage" class="btn btn-outline-secondary">Cancel</button>
    </div>
  </div>

  <TargetAchievementModal
      v-if="store.state.isModalOpenTwo"
      :contactProfileId="contactProfileId"
      :targetComponent="targetComponent"
      :isSupervisor="true"
  />

  <Loader v-if="loading"/>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import { computed } from 'vue';
import PerformanceComponentForm from '@/components/molecule/company/hrm-payroll/performance-appraisal/PerformanceComponentForm.vue';
import TreeComponentItem from '@/components/molecule/company/hrm-payroll/performance-appraisal/TreeComponentItem.vue';
import TargetAchievementTreeComponentForm from '@/components/molecule/company/hrm-payroll/performance-appraisal/TargetAchievementTreeComponentForm.vue';
import handleHospital from '@/services/modules/hospital'
import TargetAchievementModal from '@/components/molecule/company/hrm-payroll/performance-appraisal/TargetAchievementModal.vue';
import handleCompany from "@/services/modules/company";
import performanceAppraisalPdf from '@/services/utils/pdf/performanceAppraisalPdf';

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const isSupervisorAuthorized = ref(false)
const performanceAppraisalData = ref([])
const contactProfile = ref({})
const tableItems = ref([]);
const targetComponent = ref({});
const componentList = ref([]);
const contactProfileId = ref('')
const performanceAppraisalNote = ref([]);
const params = ref({
    offset: 10,
});
let saveButtonLoader = ref(false)
let saveNewButtonLoader = ref(false)
const token = computed(() => localStorage.getItem('token'));
let companyInfo = reactive({})

const {
  fetchPerformanceApprisal,
  createPerformanceApprisalScore
} = handleHrPayroll()
const { fetchNoteMasterList } = handleHospital()
const { fetchCompanyInfo } = handleCompany();
const { exportToPDF } = performanceAppraisalPdf();

let formData = ref({
  company_id: route.params.companyId,
  remarks:'',
  components: []
})

function onClickOpenTargetModal(component) {
  if (!isSupervisorAuthorized.value){
    showError("You are not authorized to access this target.");
    return
  }
  targetComponent.value = component;
  store.state.isModalOpenTwo = true
}

const setCompanyInfo = async () => {
  await fetchCompanyInfo(route.params.companyId).then((res) => {
    companyInfo = res.data;
  }).catch((error) => {
    console.log(error);
  });
}

function navigateToListPage() {
  delete route.query.contact_profile_id;
  delete route.query.performance_apprisal_setup_id;
  router.push({name: `performance-appraisal-approver`, params: route.params, query: route.query});
}

const handleSubmit = () => {
  loading.value = true;
  const payload = []
  let canEvaluate = false;

  performanceAppraisalData.value.forEach(component => {
    const masterId = component.id
    const scores = extractScores(component.performance_component_setup)
    canEvaluate = component.can_evaluate == 1;

    scores.forEach(score => {
      payload.push({
        performance_apprisal_master_id: masterId,
        performance_component_setup_id: score.id,
        performance_apprisal_note_master_id: score.performance_apprisal_note_master_id,
        score: score.score
      })
    })
  })

  let targetAchievementData = []
  componentList.value.forEach(component => {
    if (component.calculation_type != 'target'){
      return
    }
    let achievement = extractComponent(component);
    targetAchievementData.push({
      data: achievement,
      performance_component_setup_id: component.id
    });
  })

  let apprisalData = {};
  apprisalData.components = payload;
  apprisalData.achievement_data = targetAchievementData;
  apprisalData.company_id = route.params.companyId;
  apprisalData.performance_apprisal_setup_id = route.query.performance_apprisal_setup_id;
  apprisalData.user_contact_profile_id = contactProfileId.value;
  apprisalData.remarks = formData.value.remarks;
  apprisalData.can_evaluate = canEvaluate
  let query =  `?company_id=${route.params.companyId}`;

  createPerformanceApprisalScore(apprisalData, query)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
      })
      .catch(err => {
        console.log(err)
      }).finally(() => {
       loading.value = false
  })
}

const extractComponent = (node) => {
  const endpoints = []
  const traverse = (n) => {
    if (n.type === 'endpoint') {
      endpoints.push({
        performance_component_setup_id: n.id,
        qty: n.qty ? n.qty : null,
        value: n.value ? n.value : 0,
        remarks: n.remarks ? n.remarks : 0,
      })
    }
    if (n.active_child && n.active_child.length) {
      n.active_child.forEach(traverse)
    }
  }
  traverse(node)
  return endpoints
}

const extractScores = (node) => {
  const endpoints = []
  const traverse = (n) => {
    if (n.type === 'endpoint') {
      endpoints.push({
        id: n.id,
        performance_apprisal_note_master_id: n.performance_apprisal_note_master ? n.performance_apprisal_note_master.id : null,
        score: n.performance_apprisal_note_master ? n.performance_apprisal_note_master.score : 0,
      })
    }
    if (n.active_child && n.active_child.length) {
      n.active_child.forEach(traverse)
    }
  }
  traverse(node)
  return endpoints
}

const getApprisalData = async () => {
    const query = getQuery();
    loading.value = true
    componentList.value = []
    contactProfile.value = {}
    performanceAppraisalData.value = []
    isSupervisorAuthorized.value = false
    await fetchPerformanceApprisal(query).then(res => {
        if (!res.status){
            return
        }
        componentList.value = res.data.component
        contactProfile.value = res.data.contact_profile
        performanceAppraisalData.value = res.data.apprisal_data
        isSupervisorAuthorized.value = res.data.is_supervisor_authorized
        formData.value.remarks = res.data.apprisal_data[0] && res.data.apprisal_data[0].remarks;

        bindAppraisalGenerals(performanceAppraisalData.value)
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const goToPrint = async () => {
  const query = getQuery();
  loading.value = true
  componentList.value = []
  contactProfile.value = {}
  performanceAppraisalData.value = []
  await fetchPerformanceApprisal(query).then((res) => {
    if (!res.status) {
      return
    }
    componentList.value = res.data.component;
    performanceAppraisalData.value = res.data.apprisal_data
    bindAppraisalGenerals(performanceAppraisalData.value)
    contactProfile.value = res.data.contact_profile;
  }).then(() => {
    exportToPDF(companyInfo, contactProfile.value, [], componentList.value, performanceAppraisalData.value)
  }).catch((err) => {
    console.log("Something went wrong");
  }).finally(() => {
    loading.value = false
  })
}

const bindAppraisalGenerals = (appraisalData) => {
  appraisalData.forEach(master => {
    const generalsMap = {}

    master.generals.forEach(gen => {
      generalsMap[gen.performance_component_setup_id] = {
        id: gen.performance_apprisal_note_master_id,
        name: gen.note_master_name,
        score: gen.score
      }
    })

    const bindGenerals = (node) => {
      if (node.type === 'endpoint') {
        const general = generalsMap[node.id] || ''
        node.performance_apprisal_note_master = general
        node.score = general ? general.score : ''
      }

      if (node.active_child && node.active_child.length) {
        node.active_child.forEach(bindGenerals)
      }
    }

    bindGenerals(master.performance_component_setup)
  })
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&performance_apprisal_setup_id=${route.query.performance_apprisal_setup_id}`;
    query += `&user_contact_profile_id=${route.query.contact_profile_id}`;
    return query;
}

onMounted(() => {
   let noteMasterQuery = `?company_id=${route.params.companyId}&is_all=true&status=active&note_type=performance_apprisal_score`
   getApprisalData()
   setCompanyInfo()
   contactProfileId.value = route.query.contact_profile_id;
   fetchNoteMasterList(noteMasterQuery).then(res => {
    if (res.data) {
      performanceAppraisalNote.value = res.data
    }
  });
})
</script>

<style>
  .profile-info p{
    line-height: 1rem !important;
  }
</style>